import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import {
  formElementTypes,
  MAX_CHAR_LIMIT,
  userType,
} from "../../../../utils/Constant";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import QuestionTable from "../../../admin/form-templates/form-builder/preview/table";
import { updateElementAnswerRequest } from "../../../../utils/APICalls";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import dayjs, { Dayjs } from "dayjs";
import MultiInputPreview from "../../../admin/form-templates/form-builder/preview/multi-input";
import PhotosList from "./photos-list";
import TopLoader from "../../../../components/top-loader/top-loader";
import { useSelector } from "react-redux";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import { move } from "@dnd-kit/helpers";
import DragPhoto from "./drag-photo";

export default function Element({
  sectionId,
  element,
  orderId,
  addedImages,
  updateData,
  updateCanTakePhoto,
  disabled,
  showRequiredField,
}: any) {
  const [text, setText] = useState<string | null>(null);
  const [radioSelected, setRadioSelected] = useState<string>("");
  const [isRadioOtherSelected, setIsRadioOtherSelected] = useState(false);
  const [checkboxSelected, setCheckboxSelected] = useState<string[]>([]);
  const [isCheckboxOtherSelected, setIsCheckboxOtherSelected] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(null);
  const [matrixData, setMatrixData] = useState<any[]>([]);
  const [photos, setPhotos] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canTakePhotos, setCanTakePhotos] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [reorderPhotos, setReorderPhotos] = useState(false);

  const { currentUser } = useSelector((state: any) => state.user.user);

  useEffect(() => {
    if (element) {
      if (element?.type === formElementTypes.openEnd && element?.answers) {
        setText(element?.answers?.inspector?.answer);
      }
      if (element?.type === formElementTypes.singleSelect && element?.answers) {
        const exists = element?.settings?.answerOptions.includes(
          element?.answers?.inspector?.answer
        );
        if (exists) {
          setRadioSelected(element?.answers?.inspector?.answer);
        } else {
          setRadioSelected("Other");
          setIsRadioOtherSelected(true);
          setText(element?.answers?.inspector?.answer);
        }
      }
      if (
        element?.type === formElementTypes.multiSelect &&
        element?.answers &&
        element?.answers?.inspector?.answer
      ) {
        const answersArray = element?.answers?.inspector?.answer.split(",");
        const isNotOther = element?.settings?.answerOptions.includes(
          answersArray[answersArray.length - 1]
        );
        if (!isNotOther) {
          setIsCheckboxOtherSelected(true);
          setText(answersArray[answersArray.length - 1]);
        }
        setCheckboxSelected(answersArray);
      }
      if (
        element?.type === formElementTypes.singleDate &&
        element?.answers &&
        element?.answers?.inspector?.answer
      ) {
        setDate(dayjs(element?.answers?.inspector?.answer));
      }
      if (element?.type === formElementTypes.matrix) {
        const childElements: any[] = [];
        element?.childElements?.forEach((element: any) => {
          const data = {
            id: element.id,
            answer: element?.answers?.inspector?.answer || null,
          };
          childElements.push(data);
        });
        setMatrixData(childElements);
      }
      if (element?.type === formElementTypes.photoRequest && element?.answers) {
        setPhotos(element?.answers?.inspector?.photoAnswers || []);
        setText(element?.answers?.inspector?.answer || null);
        setCanTakePhotos(element?.answers?.inspector?.answer ? true : false);
        updateCanTakePhoto(
          element?.id,
          element?.answers?.inspector?.answer ? true : false
        );
      }
    }
  }, [element]); // eslint-disable-line

  useEffect(() => {
    let prevValue: string | null = null;
    if (element) {
      if (element?.type === formElementTypes.openEnd && element?.answers) {
        prevValue = element?.answers?.inspector?.answer;
      }
      if (element?.type === formElementTypes.singleSelect && element?.answers) {
        prevValue = element?.answers?.inspector?.answer;
      }
      if (element?.type === formElementTypes.multiSelect && element?.answers) {
        const answerArray = element?.answers?.inspector?.answer.split(",");
        if (answerArray) prevValue = answerArray[answerArray.length - 1];
      }
      if (element?.type === formElementTypes.photoRequest && element?.answers) {
        prevValue = element?.answers?.inspector?.answer;
      }
    }
    const delayInputTimeoutId = setTimeout(() => {
      if (text !== null && text !== prevValue) {
        const payload: any = {};
        if (
          element?.type === formElementTypes.openEnd ||
          element?.type === formElementTypes.singleSelect
        ) {
          payload.answer = text;
        } else if (element?.type === formElementTypes.multiSelect) {
          const answerArray = [...checkboxSelected];
          const isNotOther = element?.settings?.answerOptions.includes(
            answerArray[answerArray.length - 1]
          );
          if (text) {
            if (isNotOther) {
              // if other selected, then push the text in the last
              answerArray.push(text);
            } else {
              answerArray.splice(answerArray.length - 1, 1, text);
            }
          }
          payload.answer = answerArray.join(",");
        } else if (element?.type === formElementTypes.photoRequest) {
          payload.photoAnswer = { failureExplanation: text };
        }
        updateAnswer(payload);
      }
    }, 800);
    return () => clearTimeout(delayInputTimeoutId);
  }, [text, element]); // eslint-disable-line

  useEffect(() => {
    if (
      element.type === formElementTypes.photoRequest &&
      addedImages?.elementId === element.id &&
      addedImages.fileKeys &&
      addedImages.fileKeys.length > 0
    ) {
      const existingItems = element?.answers?.inspector?.photoAnswers || [];
      const newImages: any[] = [];
      addedImages.fileKeys.forEach((key: string, index: number) => {
        const newImage = {
          key,
          description: "",
          index,
        };
        newImages.push(newImage);
      });
      const payload = {
        photoAnswer: {
          newItems: newImages,
          existingItems,
        },
      };
      updateAnswer(payload);
    }
  }, [addedImages, element]); // eslint-disable-line

  const onRadioSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    setRadioSelected(value);
    if (value !== "Other") {
      const payload = {
        answer: value,
      };
      updateAnswer(payload);
    }
  };

  const onCheckboxSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value;
    let checkboxValues = [...checkboxSelected];
    if (value === "Other") {
      setIsCheckboxOtherSelected(event.target.checked);
      const isNotOther = element?.settings?.answerOptions.includes(
        checkboxValues[checkboxValues.length - 1]
      );
      if (!event.target.checked && !isNotOther) {
        checkboxValues.pop();
        setText(null);
        if (checkboxValues.length > 0) {
          const payload = {
            answer: checkboxValues.join(","),
          };
          updateAnswer(payload);
        } else {
          updateAnswer({ answer: "" });
        }
      }
    } else {
      if (checkboxSelected.length > 0) {
        if (event.target.checked) {
          checkboxValues.unshift(value);
          setCheckboxSelected(checkboxValues);
        } else {
          checkboxValues = checkboxValues.filter(
            (checkboxValue) => checkboxValue !== value
          );
          setCheckboxSelected(checkboxValues);
        }
      } else {
        checkboxValues.unshift(value);
        setCheckboxSelected(checkboxValues);
      }
      if (checkboxValues.length > 0) {
        const payload = {
          answer: checkboxValues.join(","),
        };
        updateAnswer(payload);
      } else {
        updateAnswer({ answer: "" });
      }
    }
  };

  const onDateChange = (date: Dayjs | null) => {
    setDate(date);
    if (date) {
      const formattedDate = dayjs(date).format("YYYY-MM-DD");
      if (dayjs(formattedDate).isValid()) {
        const payload = {
          answer: formattedDate,
        };
        updateAnswer(payload);
      }
    }
  };

  const onMatrixRadioSelectionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    childElement: any
  ) => {
    const value = (event.target as HTMLInputElement).value;
    const childElements = [...matrixData];
    if (childElements.length > 0) {
      const index = childElements.findIndex(
        (element) => element.id === childElement.id
      );
      if (index >= 0) {
        childElements[index].answer = value;
      } else {
        const data = {
          id: childElement.id,
          answer: value,
        };
        childElements.push(data);
      }
    } else {
      const data = {
        id: childElement.id,
        answer: value,
      };
      childElements.push(data);
    }
    setMatrixData(childElements);
    const payload = {
      childElements: childElements.filter((child) => child?.answer),
    };
    updateAnswer(payload);
  };

  const onMultiInputValueChange = (inputValue: any) => {
    const payload = {
      childElements: [inputValue],
    };
    updateAnswer(payload);
  };

  const onTableValueChange = (inputValue: any) => {
    const payload = {
      childElements: [inputValue],
    };
    updateAnswer(payload);
  };

  const onPhotoDescriptionUpdate = (value: string, key: string) => {
    if (key) {
      const updatedPhotos = JSON.parse(JSON.stringify(photos));
      const findPhoto = updatedPhotos.find((photo: any) => photo.key === key);
      findPhoto.description = value;
      const payload = {
        photoAnswer: {
          existingItems: updatedPhotos,
        },
      };
      updatePhotoAnswerOnDescriptionChange(payload, key, photos);
    }
  };

  const onPhotoDelete = (key: string) => {
    if (key) {
      const updatedPhotos = photos.filter((photo: any) => photo.key !== key);
      const payload = {
        photoAnswer: {
          existingItems: updatedPhotos,
        },
      };
      updateAnswer(payload);
    }
  };

  const updateAnswer = async (
    data: any,
    photoKey?: string,
    isPhotoReorder?: boolean
  ) => {
    const payload = {
      ...data,
      isInspectorAnswer:
        currentUser?.userType === userType.tenant ? true : false,
    };
    setIsLoading(true);
    const response = await updateElementAnswerRequest(
      orderId,
      element.id,
      payload
    );
    setIsLoading(false);
    if (response?.status === 204 || response?.status === 200) {
      if (!isPhotoReorder) {
        // if photo reorder then don't update the section else update the section
        updateData(sectionId, element.id, element.type, payload, photoKey);
      }
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const updatePhotoAnswerOnDescriptionChange = async (
    data: any,
    photoKey?: string,
    currentAnswer?: any
  ) => {
    const payload = {
      ...data,
      isInspectorAnswer:
        currentUser?.userType === userType.tenant ? true : false,
    };
    updateData(sectionId, element.id, element.type, payload, photoKey);
    setIsLoading(true);
    const response = await updateElementAnswerRequest(
      orderId,
      element.id,
      payload
    );
    setIsLoading(false);

    if (response?.status !== 200 && response?.status !== 204) {
      payload.photoAnswer.existingItems = currentAnswer;
      updateData(sectionId, element.id, element.type, payload, photoKey);
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const tableValueCheck = (element: any) => {
    let isAnswered = false;
    if (element?.childElements && element?.childElements.length > 0) {
      const filterAnswers = element?.childElements.filter(
        (child: any) => child?.answers?.inspector?.answer
      );
      if (filterAnswers.length > 0) {
        isAnswered = true;
      }
    }
    return isAnswered;
  };

  useEffect(() => {
    if (reorderPhotos) {
      onPhotoReorder(photos);
      setReorderPhotos(false);
    }
  }, [photos, reorderPhotos]); // eslint-disable-line

  const onPhotoReorder = (updatedPhotos: any[]) => {
    const payload = {
      photoAnswer: {
        existingItems: updatedPhotos,
      },
    };
    updateAnswer(payload, undefined, true);
  };

  return (
    <>
      {element?.type === formElementTypes.openEnd && (
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          placeholder="Value"
          multiline
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
          error={showRequiredField && element?.responseRequired && !text}
          slotProps={{
            htmlInput: { maxLength: MAX_CHAR_LIMIT },
            formHelperText: { sx: { width: "100%", m: 0 } },
          }}
          helperText={
            <Box display="flex" justifyContent="space-between" px={1}>
              <span>
                {showRequiredField && element?.responseRequired && !text
                  ? "This field is required."
                  : ""}
              </span>
              <span>
                {text ? text.length : 0}/{MAX_CHAR_LIMIT}
              </span>
            </Box>
          }
        />
      )}
      {element?.type === formElementTypes.singleSelect && (
        <FormControl
          sx={{ mt: 1, width: "100%" }}
          disabled={disabled}
          error={
            showRequiredField && element?.responseRequired && !radioSelected
          }
        >
          <RadioGroup
            name="radio-buttons-group"
            value={radioSelected}
            onChange={onRadioSelectionChange}
          >
            {element?.settings?.answerOptions.map((answer: string) => (
              <FormControlLabel
                key={answer}
                value={answer}
                control={
                  <Radio
                    checked={answer === radioSelected}
                    onChange={(event) => setIsRadioOtherSelected(false)}
                  />
                }
                label={answer}
              />
            ))}
            {element?.settings?.includeOtherOption && (
              <FormControlLabel
                key="other"
                value="Other"
                control={
                  <Radio
                    checked={"Other" === radioSelected}
                    onChange={(event) =>
                      setIsRadioOtherSelected(event.target.checked)
                    }
                  />
                }
                label="Other"
              />
            )}
            {isRadioOtherSelected && (
              <TextField
                sx={{ mt: 1 }}
                fullWidth
                placeholder="Value"
                multiline
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={disabled}
                error={showRequiredField && element?.responseRequired && !text}
                slotProps={{
                  htmlInput: { maxLength: MAX_CHAR_LIMIT },
                  formHelperText: { sx: { width: "100%", m: 0 } },
                }}
                helperText={
                  <Box display="flex" justifyContent="flex-end" px={1}>
                    <span>
                      {text ? text.length : 0}/{MAX_CHAR_LIMIT}
                    </span>
                  </Box>
                }
              />
            )}
          </RadioGroup>
          {showRequiredField &&
            element?.responseRequired &&
            Boolean(radioSelected) && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
        </FormControl>
      )}

      {element?.type === formElementTypes.multiSelect && (
        <FormControl
          sx={{ mt: 1, width: "100%" }}
          disabled={disabled}
          error={
            showRequiredField &&
            element?.responseRequired &&
            checkboxSelected &&
            checkboxSelected.length === 0
          }
        >
          <FormGroup>
            {element?.settings?.answerOptions.map((answer: string) => (
              <FormControlLabel
                key={answer}
                value={answer}
                control={
                  <Checkbox
                    checked={checkboxSelected.includes(answer)}
                    onChange={onCheckboxSelectionChange}
                  />
                }
                label={answer}
              />
            ))}
            {element?.settings?.includeOtherOption && (
              <FormControlLabel
                key="other"
                value="Other"
                control={
                  <Checkbox
                    checked={isCheckboxOtherSelected}
                    onChange={onCheckboxSelectionChange}
                  />
                }
                label="Other"
              />
            )}
            {isCheckboxOtherSelected && (
              <TextField
                sx={{ mt: 1 }}
                fullWidth
                placeholder="Value"
                multiline
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={disabled}
                error={showRequiredField && element?.responseRequired && !text}
                slotProps={{
                  htmlInput: { maxLength: MAX_CHAR_LIMIT },
                  formHelperText: { sx: { width: "100%", m: 0 } },
                }}
                helperText={
                  <Box display="flex" justifyContent="flex-end" px={1}>
                    <span>
                      {text ? text.length : 0}/{MAX_CHAR_LIMIT}
                    </span>
                  </Box>
                }
              />
            )}
          </FormGroup>
          {showRequiredField &&
            element?.responseRequired &&
            checkboxSelected &&
            checkboxSelected.length === 0 && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
        </FormControl>
      )}

      {element?.type === formElementTypes.matrix && (
        <Box>
          <Box sx={{ borderLeft: "1px solid #C4C4C4", mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography className="question-matrix-header-text"></Typography>
              {element?.settings?.answerOptions?.map((option: string) => (
                <Typography
                  key={option}
                  className="question-matrix-header-text"
                >
                  {option}
                </Typography>
              ))}
            </Box>
            {element?.childElements?.map((question: any, index: number) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Box
                  className="question-matrix-subquestions-layout"
                  sx={{
                    width: `calc(100%/${
                      element?.settings?.answerOptions.length + 1
                    } - 20px)`,
                  }}
                >
                  <Typography className="question-matrix-subquestion-txt">
                    {question.title}
                  </Typography>
                  <Typography
                    className="question-matrix-subquestion-defined-name"
                    noWrap
                  >
                    #{question.definedName}
                  </Typography>
                </Box>
                <RadioGroup
                  name="matrix-radio-buttons-group"
                  sx={{
                    display: "flex",
                    height: 67,
                    width: `calc(100% - 100%/${
                      element?.settings?.answerOptions.length + 1
                    })`,
                  }}
                  onChange={(e) => onMatrixRadioSelectionChange(e, question)}
                >
                  {element?.settings?.answerOptions.map((answer: string) => (
                    <FormControlLabel
                      className="question-matrix-subquestion-radio"
                      sx={{ flex: 1, mr: 0 }}
                      key={answer}
                      value={answer}
                      disabled={disabled}
                      control={
                        <Radio
                          checked={
                            question?.answers?.inspector?.answer === answer
                          }
                        />
                      }
                      label=""
                    />
                  ))}
                </RadioGroup>
              </Box>
            ))}
          </Box>
          {showRequiredField &&
            element?.responseRequired &&
            matrixData &&
            matrixData.length === 0 && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
        </Box>
      )}

      {element?.type === formElementTypes.table && (
        <Box>
          <QuestionTable
            element={element}
            inputValueChange={onTableValueChange}
            disabled={disabled}
          />
          {showRequiredField &&
            element?.responseRequired &&
            !tableValueCheck(element) && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
        </Box>
      )}

      {element?.type === formElementTypes.multiInput && (
        <MultiInputPreview
          element={element}
          inputValueChange={onMultiInputValueChange}
          disabled={disabled}
          showRequiredField={showRequiredField}
        />
      )}

      {element?.type === formElementTypes.singleDate && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ width: 300, mt: 1 }}
            format="LL"
            value={date}
            onChange={onDateChange}
            disabled={disabled}
            slotProps={{
              textField: {
                error:
                  showRequiredField &&
                  element?.responseRequired &&
                  Boolean(date),
                helperText:
                  showRequiredField &&
                  element?.responseRequired &&
                  Boolean(date) &&
                  "This field is required.",
              },
            }}
          />
        </LocalizationProvider>
      )}

      {element?.type === formElementTypes.photoRequest && (
        <>
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={canTakePhotos}
                  onChange={(event) => {
                    updateCanTakePhoto(element?.id, event.target.checked);
                    setCanTakePhotos(event.target.checked);
                  }}
                  disabled={disabled || photos.length > 0}
                />
              }
              label="Check if Unable to Take Photos"
            />
          </FormGroup>
          {canTakePhotos && (
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              placeholder="Value"
              multiline
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={disabled}
              error={
                showRequiredField &&
                element?.responseRequired &&
                canTakePhotos &&
                !text
              }
              slotProps={{
                htmlInput: { maxLength: MAX_CHAR_LIMIT },
                formHelperText: { sx: { width: "100%", m: 0 } },
              }}
              helperText={
                <Box display="flex" justifyContent="space-between" px={1}>
                  <span>
                    {showRequiredField &&
                    element?.responseRequired &&
                    canTakePhotos &&
                    !text
                      ? "This field is required."
                      : ""}
                  </span>
                  <span>
                    {text ? text.length : 0}/{MAX_CHAR_LIMIT}
                  </span>
                </Box>
              }
            />
          )}
          <DragDropProvider
            onDragEnd={(event) => {
              setReorderPhotos(true);
              setPhotos((items) => move(items, event));
            }}
          >
            <Box>
              {photos.length > 0 &&
                photos.map((photo, index) => (
                  <PhotosList
                    key={photo?.key}
                    photoData={photo}
                    id={photo?.key}
                    index={index}
                    onDescriptionUpdate={onPhotoDescriptionUpdate}
                    onDelete={onPhotoDelete}
                    disabled={disabled}
                  />
                ))}
            </Box>
            <DragOverlay>
              {(source) => (
                <DragPhoto
                  photoData={photos.find((photo) => photo?.key === source.id)}
                />
              )}
            </DragOverlay>
          </DragDropProvider>

          {showRequiredField &&
            element?.responseRequired &&
            !canTakePhotos &&
            photos &&
            photos.length === 0 && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                This field is required.
              </FormHelperText>
            )}
        </>
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {isLoading && <TopLoader />}
    </>
  );
}
